import { useEffect, useRef } from "react";

/**
 * Fires `onComplete` once, `delay` ms after `active` first becomes true.
 * Used by scenes to tell the tab strip they've finished playing so it can
 * advance to the next use case. Re-arms on remount (each tab switch).
 *
 * Pass `active: <complete> && inView` so the advance pauses when the section is
 * scrolled out of view (above or below). The "fired" guard only trips once the
 * timer actually completes, so toggling `active` off (scrolling away) cancels
 * the pending advance and lets it re-arm when you scroll back.
 */
export function useSceneFinish({
  active,
  delay,
  enabled,
  onComplete,
}: {
  active: boolean;
  delay: number;
  enabled: boolean;
  onComplete?: () => void;
}) {
  const fired = useRef(false);
  useEffect(() => {
    if (!enabled || !active || fired.current) return;
    const id = setTimeout(() => {
      fired.current = true;
      onComplete?.();
    }, delay);
    return () => clearTimeout(id);
  }, [active, delay, enabled, onComplete]);
}
